import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { userApi, type UserRoleAssignment } from "@/lib/api/admin-api";
import type { User } from "@/lib/api/admin-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EditUserDialog } from "@/components/users";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 20;

// ── Primary role label from userRoles array ──────────────────────────────────

function primaryRoleName(user: User): string {
  const roles = user.userRoles;
  if (!roles?.length) return "—";
  const nonSuper = roles.find((r) => r.role.code !== "SUPER_ADMIN");
  return (nonSuper ?? roles[0])?.role.name ?? "—";
}

// ── Per-row edit cell: lazily loads role assignments on open ─────────────────

function EditCell({ user }: { user: User }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: roleAssignments = [], isLoading: rolesLoading } = useQuery<UserRoleAssignment[]>({
    queryKey: ["users", user.id, "roles"],
    queryFn: () => userApi.getRoles(String(user.id)),
    enabled: open,
  });

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0"
        aria-label={t`Edit user`}
        onClick={() => setOpen(true)}
      >
        {open && rolesLoading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Pencil className="h-3.5 w-3.5" />
        )}
      </Button>

      {open && !rolesLoading && (
        <EditUserDialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) {
              void queryClient.invalidateQueries({ queryKey: ["users", "perms-tab"] });
              void queryClient.invalidateQueries({ queryKey: ["users", user.id, "roles"] });
            }
          }}
          user={user}
          roleAssignments={roleAssignments}
        />
      )}
    </>
  );
}

// ── Main tab component ───────────────────────────────────────────────────────

export function UserAssignmentsTab() {
  const { t } = useLingui();
  const { isAuthenticated } = useAuth();

  const [searchRaw, setSearchRaw] = useState("");
  const [page, setPage] = useState(1);

  // Debounce: reset page on new search, use value directly (fast enough for server-side)
  const search = searchRaw.trim();

  const { data: usersPage, isLoading, isFetching } = useQuery({
    queryKey: ["users", "perms-tab", page, search],
    queryFn: () => userApi.paginate({ page, limit: PAGE_SIZE, search: search || undefined }),
    enabled: isAuthenticated,
    placeholderData: (prev) => prev,
  });

  const users = usersPage?.data ?? [];
  const meta  = usersPage?.meta;
  const totalPages = meta?.totalPages ?? 1;

  function handleSearchChange(value: string) {
    setSearchRaw(value);
    setPage(1);
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Search bar */}
      <div className="flex items-center gap-3 px-1">
        <Input
          placeholder={t`Search by name or email…`}
          value={searchRaw}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="max-w-sm"
        />
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-(--color-text-secondary)" />}
        {meta && (
          <span className="text-sm text-(--color-text-secondary) ml-auto">
            {meta.total} {t`users`}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-x-auto">
        <Table className="min-w-160">
          <TableHeader>
            <TableRow className="bg-(--color-surface-muted) hover:bg-(--color-surface-muted)">
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary)">{t`Name`}</TableHead>
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary)">{t`Email`}</TableHead>
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary) hidden md:table-cell">
                {t`Organization`}
              </TableHead>
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary)">{t`Role`}</TableHead>
              <TableHead className="h-auto px-4 py-3 text-center font-medium text-(--color-text-secondary)">{t`Status`}</TableHead>
              <TableHead className="h-auto px-4 py-3 text-right font-medium text-(--color-text-secondary)">{t`Actions`}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} className="last:border-b-0 hover:bg-(--color-surface-muted)/50">
                <TableCell className="px-4 py-3 font-medium">
                  {[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}
                </TableCell>
                <TableCell className="px-4 py-3 text-(--color-text-secondary) max-w-56 truncate">
                  {user.email}
                </TableCell>
                <TableCell className="px-4 py-3 text-(--color-text-secondary) hidden md:table-cell">
                  {user.organization?.name ?? "—"}
                </TableCell>
                <TableCell className="px-4 py-3">
                  <span className="text-sm">{primaryRoleName(user)}</span>
                </TableCell>
                <TableCell className="px-4 py-3 text-center">
                  <Badge variant={user.isActive ? "default" : "neutral"}>
                    {user.isActive ? t`Active` : t`Inactive`}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-3 text-right">
                  <EditCell user={user} />
                </TableCell>
              </TableRow>
            ))}

            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="px-4 py-16 text-center text-sm text-(--color-text-secondary)">
                  {search ? t`No users match your search.` : t`No users found.`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 px-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isFetching}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-(--color-text-secondary)">
            {t`Page`} {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isFetching}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
